// Staff alerts on the server's iPad (Expo.dc.html "On the server's iPad";
// docs/DINING_API_2026-10-28.md §13.3). An alert IS a restaurant event:
// GET /api/dining/alerts lists the ones this person has not acknowledged;
// the stream says when to read them again. New ones slide in with a chime,
// chime again every 60 s and turn red after 3 minutes until acknowledged.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STATUS } from '@/lib/diningEvents';
import { api, errorText } from '../lib/api';
import { channelLine, clock, isChannelTicket, money, plural, secondsSince, ticketTitle } from '../lib/format';
import { chime } from '../lib/sound';
import { useLive } from '../lib/useLive';
import { C } from '../theme/tokens';
import { useNow, useToast } from './ui';
import { IconAlert, IconBell, IconCard, IconChat, IconClose, IconShield, IconWifi } from './icons';

export const ALERT_EVENTS = [
  'ticket.ready', 'ticket.served', 'ticket.recalled', 'ticket.cancelled', 'session.checkout_requested',
  'session.released', 'session.merged', 'session.transferred', 'payment.declined', 'payment.started', 'payment.approved',
  'payment.timed_out', 'payment.error', 'payment.cancelled',
  'payment.tender', 'check.voided', 'approval.requested', 'approval.granted', 'alert.acknowledged',
  'chat.message', 'chat.read', 'chat.closed',
];

// alerts that chime the urgent sound (money that needs a person now)
const URGENT = ['payment_declined', 'payment_unknown'];

const KIND = {
  food_ready: { icon: IconBell, bg: C.amber, fg: '#1F1300' },
  checkout_requested: { icon: IconCard, bg: C.violetHi, fg: '#FFFFFF' },
  payment_declined: { icon: IconAlert, bg: C.redHi, fg: '#FFFFFF' },
  payment_unknown: { icon: IconCard, bg: C.amber, fg: '#1F1300' },
  approval_requested: { icon: IconShield, bg: '#A78BFA', fg: '#1E1033' },
  chat_message: { icon: IconChat, bg: C.blueHi, fg: '#FFFFFF' },
};

const APPROVAL_LABEL = {
  'items.void': 'void an item', 'discounts.apply': 'a discount', 'orders.modify_sent': 'change a sent item', 'payments.refund': 'a refund',
  'payments.void': 'void a card payment', 'orders.cancel': 'cancel an order', 'checks.service_charge': 'a service charge',
  'kitchen.clear_old': 'clear old tickets', 'timeclock.edit': 'a time-clock change', 'settlement.batch_out': 'batch out', 'cash.paid_out': 'a paid out', 'cash.close_drawer': 'closing a drawer',
};

export function alertText(a) {
  const table = a.table_number ? `Table ${a.table_number}` : 'A table';
  if (a.kind === 'food_ready' && isChannelTicket(a)) {
    const items = (a.items || []).filter((i) => i.status !== 'served');
    return {
      title: `${ticketTitle(a)} · ready for pickup`,
      sub: [channelLine(a), plural(items.length, 'item'), clock(a.created_at), a.station?.name].filter(Boolean).join(' · '),
    };
  }
  if (a.kind === 'food_ready') {
    const items = (a.items || []).filter((i) => i.status !== 'served');
    const names = items.map((i) => (i.quantity > 1 ? `${i.name} ×${i.quantity}` : i.name));
    const list = names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
    return {
      title: `${table} · ${list || 'food'} ready`,
      sub: [`At the pass · ${plural(items.length, 'item')}`, clock(a.created_at), a.station?.name, a.server_name].filter(Boolean).join(' · '),
    };
  }
  if (a.kind === 'checkout_requested') {
    return { title: `${table} · check requested`, sub: [a.server_name, clock(a.created_at)].filter(Boolean).join(' · ') };
  }
  if (a.kind === 'payment_declined') {
    return {
      title: `${table} · card declined${a.check_no ? ` on check ${a.check_no}` : ''}`,
      sub: [a.amount_cents != null ? money(a.amount_cents) : null, clock(a.created_at), 'Try again or take another payment'].filter(Boolean).join(' · '),
    };
  }
  if (a.kind === 'payment_unknown') {
    return {
      title: `${table} · card result unknown${a.check_no ? ` on check ${a.check_no}` : ''} — check the terminal`,
      sub: [a.amount_cents != null ? money(a.amount_cents) : null, clock(a.created_at), 'Look at the terminal before charging again'].filter(Boolean).join(' · '),
    };
  }
  if (a.kind === 'chat_message') {
    const r = a.reservation_request;
    return {
      title: `${a.guest_name || 'A guest'} · ${a.topic === 'reservation' ? 'asks for a table' : 'new message'}`,
      sub: [r ? `${r.party_size} · ${r.date} · ${r.time}` : null, a.preview, clock(a.created_at)].filter(Boolean).join(' · '),
    };
  }
  if (a.kind === 'approval_requested') {
    return {
      title: `Approval asked · ${APPROVAL_LABEL[a.action] || a.action}`,
      sub: [a.requested_by_name ? `by ${a.requested_by_name}` : null, a.table_number ? table : null, a.reason, clock(a.created_at)].filter(Boolean).join(' · '),
    };
  }
  return { title: table, sub: clock(a.created_at) };
}

export function alertsEnabledFor(me) {
  if (!me) return false;
  if ((me.permissions || []).includes('chat.reply')) return true; // a host gets guest chat alerts
  return !['kitchen', 'host'].includes(me.role);
}

export function useAlerts({ enabled = true, includeAcknowledged = false } = {}) {
  const live = useLive(() => api.get('/dining/alerts', { include_acknowledged: includeAcknowledged ? 'true' : undefined }),
    [includeAcknowledged], { enabled, types: ALERT_EVENTS });
  const toast = useToast();
  const acknowledge = useCallback(async (alert) => {
    try {
      await api.post(`/dining/alerts/${alert.seq}/acknowledge`);
    } catch (e) {
      if (e.status !== 404) toast.error(errorText(e));
    }
    live.reload();
  }, [live, toast]);
  return { enabled, loaded: live.data !== null, alerts: live.data?.alerts || [], error: live.error, loading: live.loading, reload: live.reload, acknowledge, streamStatus: live.streamStatus };
}

function openTarget(a, canOrder) {
  if (a.kind === 'chat_message') return `/dining/chat?c=${a.conversation_id}`;
  if (a.kind === 'food_ready' && isChannelTicket(a)) return '/dining/expo';
  if (a.session_id && canOrder) return `/dining/table/${a.session_id}`;
  if (a.table_id) return `/dining/floor?table=${a.table_id}`;
  return '/dining/floor';
}

function AlertCard({ a, big, onAck, onOpen, now }) {
  const k = KIND[a.kind] || KIND.food_ready;
  const Icon = k.icon;
  const { title, sub } = alertText(a);
  const age = secondsSince(a.created_at, now);
  const late = age > 180;
  const others = (a.acknowledged_by || []).map((x) => x.name).filter(Boolean);
  return (
    <div role="status" className="c29-in" style={{
      background: late ? '#7F1D1D' : C.ink, color: '#FFFFFF', borderRadius: big ? 20 : 16, padding: big ? 16 : 14,
      display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 50px rgba(0,0,0,.35)', border: late ? '1.5px solid #EF4444' : '1px solid rgba(255,255,255,.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 44, height: 44, flex: 'none', borderRadius: '50%', background: k.bg, color: k.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={22} sw={2.4} /></span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: big ? 18 : 16, fontWeight: 800, lineHeight: 1.25 }}>{title}</span>
          <span style={{ fontSize: 13, color: '#D6D3D1', lineHeight: 1.35 }}>{sub}</span>
          {others.length > 0 && <span style={{ fontSize: 12.5, color: '#FDBA74', fontWeight: 600 }}>On it: {others.join(', ')}</span>}
        </div>
        <span style={{ fontSize: 12, color: late ? '#FCA5A5' : '#A8A29E', fontWeight: 700, flex: 'none' }}>{age < 60 ? 'now' : `${Math.floor(age / 60)}m`}</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="c29-press" onClick={() => onAck(a)} style={{ flex: 1, height: 48, border: 'none', borderRadius: 12, background: C.amber, color: '#1F1300', fontSize: 15, fontWeight: 800 }}>Acknowledge</button>
        {(a.table_id || a.session_id || a.kind === 'chat_message') && (
          <button type="button" className="c29-press" onClick={() => onOpen(a)} style={{ flex: 1, height: 48, borderRadius: 12, border: '1px solid #44403C', background: 'transparent', color: '#FFFFFF', fontSize: 15, fontWeight: 700 }}>
            {a.kind === 'chat_message' ? 'Open chat' : `Open ${a.table_number ? `Table ${a.table_number}` : 'table'}`}
          </button>
        )}
      </div>
    </div>
  );
}

// The slide-in toasts + the tray. `trayOpen` / `onTray` are controlled by the screen's bell button.
export function AlertCenter({ me, state, trayOpen, onTray, soundOn = true, streamStatus, dark = true }) {
  const { enabled, loaded, alerts, acknowledge, error } = state;
  const navigate = useNavigate();
  const now = useNow(5000);
  const seen = useRef(null);
  const [fresh, setFresh] = useState([]); // seqs shown as toasts
  const lastChime = useRef(0);
  const canOrder = (me?.permissions || []).includes('orders.create');
  const [back, setBack] = useState(null);
  const lastStatus = useRef(streamStatus);
  const downSince = useRef(null);

  // new alerts slide in (the first load only fills `seen`)
  useEffect(() => {
    if (!loaded) return;
    const ids = new Set(alerts.map((a) => a.seq));
    if (seen.current === null) { seen.current = ids; return; }
    const added = alerts.filter((a) => !seen.current.has(a.seq));
    seen.current = ids;
    setFresh((list) => [...list.filter((s) => ids.has(s)), ...added.map((a) => a.seq)].slice(-3));
    if (added.length && soundOn) { chime(added.some((a) => URGENT.includes(a.kind)) ? 'alert' : 'ready'); lastChime.current = Date.now(); }
  }, [alerts, loaded, soundOn]);

  // unacknowledged alerts chime again every 60 s
  useEffect(() => {
    if (!soundOn || !alerts.length) return;
    if (Date.now() - lastChime.current >= 60000) {
      lastChime.current = Date.now();
      chime('ready');
    }
  }, [now, alerts.length, soundOn]);

  // "Back online" after the stream was down for a while
  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = streamStatus;
    if (streamStatus !== STATUS.LIVE) { if (downSince.current === null) downSince.current = Date.now(); return; }
    if (was && was !== STATUS.LIVE && downSince.current && Date.now() - downSince.current > 4000) {
      setBack(Date.now());
      setTimeout(() => setBack(null), 5000);
    }
    downSince.current = null;
  }, [streamStatus]);

  const open = (a) => { setFresh((l) => l.filter((s) => s !== a.seq)); onTray && onTray(false); navigate(openTarget(a, canOrder)); };
  const ack = (a) => { setFresh((l) => l.filter((s) => s !== a.seq)); acknowledge(a); };
  const toasts = useMemo(() => alerts.filter((a) => fresh.includes(a.seq)), [alerts, fresh]);

  if (!enabled) return null;
  return (
    <>
      {!trayOpen && (toasts.length > 0 || back) && (
        <div style={{ position: 'fixed', top: 80, right: 16, width: 'min(440px, calc(100vw - 32px))', zIndex: 70, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {back && (
            <div className="c29-in" role="status" style={{ background: '#FFFFFF', color: C.ink, borderRadius: 16, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, border: `1px solid ${C.border}`, boxShadow: '0 12px 30px rgba(0,0,0,.2)' }}>
              <span style={{ width: 34, height: 34, flex: 'none', borderRadius: '50%', background: C.greenSoft, color: C.greenInk, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconWifi size={18} /></span>
              <div style={{ display: 'flex', flexDirection: 'column' }}><span style={{ fontSize: 15, fontWeight: 700 }}>Back online · caught up</span><span style={{ fontSize: 12.5, color: C.muted }}>Everything missed while offline was replayed.</span></div>
            </div>
          )}
          {toasts.map((a, i) => <AlertCard key={a.seq} a={a} big={i === toasts.length - 1} onAck={ack} onOpen={open} now={now} />)}
        </div>
      )}
      {trayOpen && (
        <div className="c29-fade" onPointerDown={(e) => { if (e.target === e.currentTarget) onTray(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 65, background: 'rgba(0,0,0,.45)', display: 'flex', justifyContent: 'flex-end' }}>
          <aside role="dialog" aria-label="Alerts" className="c29-sheet" style={{ width: 'min(460px, 100vw)', height: '100%', background: dark ? C.panel : C.paper, borderLeft: `1px solid ${dark ? C.line : C.border}`, display: 'flex', flexDirection: 'column' }}>
            <div style={{ height: 68, flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', borderBottom: `1px solid ${dark ? C.line : C.border}` }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: dark ? C.textHi : C.ink, flex: 1 }}>Alerts</span>
              <span style={{ fontSize: 13, color: dark ? C.textLo : C.muted }}>{alerts.length ? `${alerts.length} open` : 'All clear'}</span>
              <button type="button" aria-label="Close alerts" onClick={() => onTray(false)} style={{ width: 44, height: 44, borderRadius: 12, border: `1px solid ${dark ? C.line2 : C.border}`, background: 'transparent', color: dark ? C.textMid : C.ink3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} /></button>
            </div>
            <div className="c29-scroll" style={{ flex: 1, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {error && <div role="alert" style={{ color: '#FCA5A5', fontSize: 14 }}>{errorText(error)}</div>}
              {!alerts.length && !error && (
                <div style={{ textAlign: 'center', padding: '48px 16px', color: dark ? C.textLo : C.muted, fontSize: 14, lineHeight: 1.5 }}>
                  No open alerts. Ready food, check requests, declined cards, card results to check and guest chat messages show up here.
                </div>
              )}
              {alerts.map((a) => <AlertCard key={a.seq} a={a} onAck={ack} onOpen={open} now={now} />)}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export function AlertBell({ count, onClick, dark = true }) {
  return (
    <button type="button" onClick={onClick} aria-label={count ? `Alerts: ${count} open` : 'Alerts'} className="c29-press"
      style={{ position: 'relative', width: 44, height: 44, borderRadius: 12, border: `1px solid ${dark ? C.line2 : C.border}`, background: dark ? C.panel2 : '#FFFFFF', color: dark ? C.text : C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
      <IconBell size={20} />
      {count > 0 && (
        <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, padding: '0 5px', borderRadius: 10, background: C.amber, color: '#1F1300', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${dark ? C.bar : '#FFFFFF'}` }}>{count}</span>
      )}
    </button>
  );
}
